import { assignRef } from "@shared/util/assign-ref"
import { tracked } from "@supergrain/kernel/react"
import { generateJSON, posToDOMRect, type FocusPosition } from "@tiptap/core"
import { Editor, useEditor, useEditorState } from "@tiptap/react"
import type * as filestack from "filestack-js"
import { useEffect, useMemo, useRef, type Ref } from "react"
import { useLinkDialog } from "./editor-ui/link/use-link-dialog"
import { headlessCardValueExtensions, noteTiptapExtensions } from "./extensions"
import {
  noteDescriptionIsShowing,
  readNoteWords,
  type NoteWords,
} from "./extensions/date-annotation-note/date-annotation-note-extension"
import type { GoogleDrivePickedFile } from "./extensions/link/link-attrs"
import styles from "./NoteTiptap.module.css"
import {
  EMPTY_COMMENT_ACTIONS,
  type DerivedPermissions,
  type UnifiedTiptapUiValue,
} from "./unified-tiptap-ui-context"
import UnifiedTiptapRenderer from "./UnifiedTiptapRenderer"
import { dragClipboardDegradedSlice, dragClipboardSlice, getDragClipboard } from "./util/clipboard"
import { defuseCrossEditorDragSource } from "./util/drag-and-drop"
import { isSubmitShortcut } from "./util/submit-shortcut"

export interface NoteTiptapProps {
  permissions: DerivedPermissions
  annotationId: string
  color: string
  dateString: string
  initialTitle: string
  initialDescription: string
  /** The note's words when the editor loses focus, in the shape the annotation stores them. */
  onCommit: (words: NoteWords) => void
  onDeleteNote?: () => void
  /**
   * Whether to hide the note's Add and Menu pills. Pass true for a note being written before
   * it is one — there is nothing yet to add to or delete.
   */
  hideButtons?: boolean
  /** What the empty title and empty body prompt with, when not the note's own words. */
  prompts?: { title: string; description: string }
  /** Names the editable region. A note painted on a day is read as text; one being written
   * is a field, and a field needs a name. */
  ariaLabel?: string
  /** Id of the element saying how the field works — that Enter opens the description. */
  ariaDescribedBy?: string
  /** Runs on Mod-Enter, for a note the user submits rather than commits by leaving. */
  onSubmitShortcut?: () => void
  /** Every change, for a caller that keeps the words as they are typed. */
  onUpdate?: (words: NoteWords) => void
  autofocus?: FocusPosition
  editorRef?: Ref<Editor | null>
  filestackClient?: filestack.Client
  addFromGoogleDrive?: () => Promise<GoogleDrivePickedFile[]>
}

export const NoteTiptap = tracked((props: NoteTiptapProps) => {
  const latestPropsRef = useRef(props)
  latestPropsRef.current = props

  const linkDialog = useLinkDialog()

  const createInitialDoc = () => {
    // A note's blocks live as HTML on the annotation, that being the only field it has. The
    // card-value extensions carry the same content spec the note node uses after its title, so
    // the parse lands blocks this doc can hold — the same conversion the legacy card migration
    // runs (convertCardValueHtml in unified-migration.ts). An empty note parses to the one
    // paragraph `block+` requires, which is the line the user types into.
    const blocks =
      generateJSON(props.initialDescription, headlessCardValueExtensions()).content ?? []

    return {
      type: "doc",
      content: [
        {
          type: "dateAnnotationNote",
          attrs: {
            id: props.annotationId,
            color: props.color,
            dateString: props.dateString,
          },
          content: [
            {
              type: "dateAnnotationNoteTitle",
              content: props.initialTitle ? [{ type: "text", text: props.initialTitle }] : [],
            },
            ...blocks,
          ],
        },
      ],
    }
  }

  const commitCurrentState = (ed: Editor) => {
    const words = readNoteWords(ed.state)
    if (words !== null) latestPropsRef.current.onCommit(words)
  }

  const editor = useEditor({
    extensions: noteTiptapExtensions({
      openLinkDialog: linkDialog.open,
      filestackClient: props.filestackClient ?? null,
    }),
    editable: props.permissions.canEdit,
    content: createInitialDoc(),
    autofocus: props.autofocus ?? false,
    editorProps: {
      attributes: {
        ...(props.ariaLabel ? { "aria-label": props.ariaLabel } : {}),
        ...(props.ariaDescribedBy ? { "aria-describedby": props.ariaDescribedBy } : {}),
      },
      handleKeyDown(_view, event) {
        const onSubmitShortcut = latestPropsRef.current.onSubmitShortcut
        if (!onSubmitShortcut || !isSubmitShortcut(event)) return false
        event.preventDefault()
        onSubmitShortcut()
        return true
      },
      handleDOMEvents: {
        focusout() {
          setTimeout(() => {
            if (editor) commitCurrentState(editor)
          }, 0)
        },
        // A hidden description has nothing for a press below the title to land on, so the
        // caret would go to the title instead. Opening it here is the mouse's way in — Enter
        // from the title is the keyboard's. On click, not mousedown: ProseMirror places the
        // caret from the coordinates as the press ends, and the description opening under the
        // pointer moves what those coordinates mean.
        click(view, event) {
          if (noteDescriptionIsShowing(view.state)) return false
          // The note's own buttons ride inside it as non-editable chrome.
          if ((event.target as HTMLElement | null)?.closest('[contenteditable="false"]')) {
            return false
          }
          const title = view.state.doc.firstChild?.firstChild
          if (!title) return false
          // Below the title, and only below it: a press on the padding above it is a press on
          // the title.
          if (event.clientY <= posToDOMRect(view, 1, 1 + title.nodeSize).bottom) return false

          // Same landing spot as Enter from the title: the first position inside the
          // description, not the boundary between the two.
          editor.commands.setTextSelection(1 + title.nodeSize + 1)
          view.focus()
          return true
        },
      },
      handleDrop(view) {
        const clipboard = getDragClipboard()
        if (!clipboard || clipboard.sourceEditor === editor) return false
        // A drag from another editor. This editor never runs handleSliceDrop, whose
        // finishCrossEditorDrag is what normally defuses Tiptap's post-drop deleteRange
        // timers — without this, the dragged card is deleted from its lesson ~10ms
        // after the drop (pinned in drag-and-drop/note-editor-drop.browser.test.tsx).
        // Never with a delete: a cross-schema drop lands at most a degraded copy, so
        // taking the original from its lesson would lose it.
        defuseCrossEditorDragSource(clipboard.sourceEditor)
        // Falling through lets PM insert what this schema can hold — exact content
        // as-is, a card as its blocks. Claim only a drop that would land NOTHING (an
        // atom widget degrades to nothing), matching the cursor that painted nowhere.
        const slice =
          dragClipboardSlice(clipboard, view.state.schema) ??
          dragClipboardDegradedSlice(clipboard, view.state.schema)
        return slice.content.size === 0
      },
    },
    onUpdate({ editor }) {
      const words = readNoteWords(editor.state)
      if (words !== null) latestPropsRef.current.onUpdate?.(words)
    },
  })

  // Tiptap's own subscription: this re-renders when the answer changes, and nowhere is the
  // answer stored. The editor's click handler asks the same question of the same state.
  const showsDescription = useEditorState({
    editor,
    selector: ({ editor }) => (editor === null ? false : noteDescriptionIsShowing(editor.state)),
  })

  useEffect(() => {
    assignRef(props.editorRef, editor)
    return () => assignRef(props.editorRef, null)
  }, [editor])

  // Rebuilt from the two flags rather than passed through, so a caller handing us a fresh
  // permissions object every render doesn't churn the context value below.
  const permissions: DerivedPermissions = useMemo(
    () => ({ canEdit: props.permissions.canEdit, canComment: props.permissions.canComment }),
    [props.permissions.canEdit, props.permissions.canComment],
  )

  const ui: Omit<UnifiedTiptapUiValue, "arbiter" | "setCardOverlayOpen"> = useMemo(
    () => ({
      permissions,
      commentActions: EMPTY_COMMENT_ACTIONS,
      currentUserId: null,
      filestackClient: props.filestackClient,
      addFromGoogleDrive: props.addFromGoogleDrive,
      onDeleteNote: props.onDeleteNote,
      hideButtons: props.hideButtons,
      notePrompts: props.prompts,
    }),
    [
      permissions,
      props.filestackClient,
      props.addFromGoogleDrive,
      props.onDeleteNote,
      props.hideButtons,
      props.prompts,
    ],
  )

  const floatingPortal = useMemo(
    () => document.getElementById("unified-tiptap-floating-portal")!,
    [],
  )

  return (
    <UnifiedTiptapRenderer
      editor={editor}
      unifiedTiptapUiValue={ui}
      linkDialog={linkDialog}
      floatingPortal={floatingPortal}
      className={showsDescription ? undefined : styles.titleOnly}
    />
  )
})

export default NoteTiptap
